import React from "react";
import { formatarParaReais } from "../../services/utilitarios";
import { tokens } from "../modal-form/tokens";

// Espelha Locacoes.Domain.Dto.TipoItemConciliacao
export enum TipoItemConciliacao {
    Parcela = 0,
    Lancamento = 1,
}

export interface CobrancaConciliavel {
    id: string;
    valor: number;
    vencimento: string;
    dataPagamento?: string;
    status?: string;
    descricao?: string;
    formaDePagamento?: string;
    externalReference?: string;
    idAssinatura?: string;
    /** Só no cadastro: a assinatura de origem segue ativa (true) ou já foi excluída/encerrada (false). */
    assinaturaAtiva?: boolean;
    assinaturaDescricao?: string;
    idCategoriaSugerida?: string;
    parecidaComAluguel?: boolean;
    estaPaga: boolean;
    estaEmAberto: boolean;
    removida: boolean;
    disponivel: boolean;
    donoTipo?: TipoItemConciliacao;
    donoId?: string;
    donoDescricao?: string;
    donoIdLocacao?: string;
    donoEhDestaLocacao?: boolean;
}

export enum AcaoConciliacao {
    VincularItemExistente = 0,
    CriarLancamento = 1,
}

export interface CategoriaConciliacao {
    id: string;
    nome: string;
    /** É a categoria de caução da empresa (pela chave; o nome pode ser outro, ex.: "ADESÃO"). */
    ehCaucao?: boolean;
}

export interface ItemConciliavel {
    tipo: TipoItemConciliacao;
    id: string;
    descricao?: string;
    valor: number;
    vencimento: string;
    status?: string;
    estaPago: boolean;
    asaasPaymentIdAtual?: string;
}

export interface ConciliacaoCobrancas {
    cliente?: { id?: string; nome?: string; email?: string };
    cobrancas: CobrancaConciliavel[];
    itens: ItemConciliavel[];
    categorias?: CategoriaConciliacao[];
    consultaIndisponivel: boolean;
    mensagemIndisponibilidade?: string;
}

/**
 * Valor do select de cada cobrança. Duas formas, porque são duas decisões diferentes:
 *   "item:<tipo>:<id>"  → esta cobrança é um item que já existe no sistema
 *   "cat:<idCategoria>" → esta cobrança vira um lançamento novo nessa categoria (caso da migração)
 */
export type ChaveItem = string;

export const chaveDoItem = (item: ItemConciliavel): ChaveItem => `item:${item.tipo}:${item.id}`;
export const chaveDaCategoria = (idCategoria: string): ChaveItem => `cat:${idCategoria}`;
export const ehChaveDeCategoria = (chave?: string) => !!chave && chave.startsWith('cat:');

interface Props {
    dados?: ConciliacaoCobrancas | null;
    carregando?: boolean;
    /** Escolhas do operador: id da cobrança no Asaas → item local que ela representa. */
    vinculos: Record<string, ChaveItem>;
    onAlterarVinculo: (idCobranca: string, chaveItem: ChaveItem | null) => void;
}

const STATUS_TEXTO: Record<string, string> = {
    PENDING: "aguardando pagamento",
    OVERDUE: "vencida",
    RECEIVED: "recebida",
    CONFIRMED: "confirmada",
    RECEIVED_IN_CASH: "recebida em dinheiro",
    REFUNDED: "estornada",
    REFUND_REQUESTED: "estorno pedido",
    REFUND_IN_PROGRESS: "estorno em andamento",
    CHARGEBACK_REQUESTED: "chargeback aberto",
    CHARGEBACK_DISPUTE: "chargeback em disputa",
    AWAITING_RISK_ANALYSIS: "em análise de risco",
    AWAITING_CHARGEBACK_REVERSAL: "aguardando reversão de chargeback",
    DELETED: "removida",
};

const FORMA_TEXTO: Record<string, string> = {
    BOLETO: "boleto",
    PIX: "pix",
    CREDIT_CARD: "cartão",
    DEBIT_CARD: "cartão de débito",
    UNDEFINED: "o cliente escolhe",
};

const formatarData = (data?: string) => {
    if (!data) return "";
    const [ano, mes, dia] = String(data).substring(0, 10).split("-");
    return dia && mes && ano ? `${dia}/${mes}/${ano}` : String(data);
};

const caixa: React.CSSProperties = {
    padding: tokens.spacingMd,
    borderRadius: tokens.radiusMd,
    fontSize: tokens.fontSm,
    background: tokens.bgLight,
    border: `1px solid ${tokens.borderLight}`,
    color: tokens.textSecondary,
};

const alerta: React.CSSProperties = {
    ...caixa,
    background: tokens.warningBg,
    border: `1px solid ${tokens.warningColor}`,
};

function Etiqueta({ texto, cor, fundo }: { texto: string; cor: string; fundo: string }) {
    return (
        <span style={{
            display: "inline-block", padding: "0.1rem 0.45rem", borderRadius: tokens.radiusSm,
            fontSize: tokens.fontXs, fontWeight: 600, color: cor, background: fundo, whiteSpace: "nowrap",
        }}>
            {texto}
        </span>
    );
}

function EtiquetaStatus({ cobranca }: { cobranca: CobrancaConciliavel }) {
    const texto = STATUS_TEXTO[cobranca.status || ""] || cobranca.status || "";
    if (cobranca.estaPaga) return <Etiqueta texto={texto} cor="#1b6b3a" fundo="#e6f6ec" />;
    if (cobranca.removida) return <Etiqueta texto={texto} cor={tokens.textMuted} fundo={tokens.bgLight} />;
    if (cobranca.status === "OVERDUE") return <Etiqueta texto={texto} cor="#8a1f1f" fundo="#fdecec" />;
    return <Etiqueta texto={texto} cor="#8a5a00" fundo="#fff4e6" />;
}

/**
 * Conciliação manual das cobranças do Asaas com os itens da locação.
 *
 * Por que existe: não há regra confiável de casamento automático para cobrança avulsa. Caução paga no
 * painel, acerto combinado por fora e boleto antigo têm valor e data quaisquer, então quem sabe o que é
 * o quê é o operador. Sem esta tela, o sistema criava uma segunda cobrança da mesma dívida e o cliente
 * recebia dois boletos.
 */
export default function ConciliacaoCobrancasAsaas({ dados, carregando, vinculos, onAlterarVinculo }: Props) {
    if (carregando) {
        return <div style={caixa}>Consultando as cobranças deste cliente no Asaas...</div>;
    }

    if (!dados) return null;

    const cobrancas = dados.cobrancas || [];
    const itens = dados.itens || [];

    const disponiveis = cobrancas.filter((c) => c.disponivel);
    const comDono = cobrancas.filter((c) => !c.disponivel && !c.removida);

    // Um item local só pode receber uma cobrança por vez: as chaves de ITEM já escolhidas saem das outras
    // listas, senão duas cobranças cairiam no mesmo item e a segunda sobrescreveria o vínculo da primeira.
    // Categoria não entra nessa trava: duas cobranças podem virar dois lançamentos da mesma categoria.
    const escolhidos = new Set(Object.values(vinculos).filter((chave) => !ehChaveDeCategoria(chave)));
    const categorias = dados.categorias || [];

    const itemPorChave = new Map(itens.map((i) => [chaveDoItem(i), i]));

    const itensOferecidos = (escolhaAtual: string) => itens.filter((item) => {
        const chave = chaveDoItem(item);
        return chave === escolhaAtual || !escolhidos.has(chave);
    });

    const rotuloItem = (item: ItemConciliavel) => {
        const tipo = item.tipo === TipoItemConciliacao.Parcela ? "Parcela" : "Lançamento";
        const pago = item.estaPago ? " · já baixado" : "";
        const jaTem = item.asaasPaymentIdAtual ? " · já tem cobrança" : "";
        return `${tipo}: ${item.descricao || "(sem descrição)"} · ${formatarParaReais(item.valor)} · ${formatarData(item.vencimento)}${pago}${jaTem}`;
    };

    return (
        <div>
            {dados.consultaIndisponivel && (
                <div style={{ ...alerta, marginBottom: tokens.spacingMd }}>
                    <strong>Não foi possível consultar o Asaas agora.</strong>
                    <div style={{ marginTop: tokens.spacingXs }}>
                        {dados.mensagemIndisponibilidade || "A lista pode estar incompleta. Tente de novo antes de decidir."}
                    </div>
                </div>
            )}

            {!dados.consultaIndisponivel && dados.mensagemIndisponibilidade && (
                <div style={{ ...caixa, marginBottom: tokens.spacingMd }}>{dados.mensagemIndisponibilidade}</div>
            )}

            {dados.cliente?.nome && (
                <div style={{ fontSize: tokens.fontSm, color: tokens.textMuted, marginBottom: tokens.spacingSm }}>
                    Cliente no Asaas: <strong style={{ color: tokens.textSecondary }}>{dados.cliente.nome}</strong>
                </div>
            )}

            {disponiveis.length === 0 && !dados.consultaIndisponivel && (
                <div style={caixa}>
                    Nenhuma cobrança deste cliente está sem vínculo no sistema. Não há o que conciliar.
                </div>
            )}

            {disponiveis.length > 0 && (
                <>
                    <div style={{ fontSize: tokens.fontBase, color: tokens.textPrimary, marginBottom: tokens.spacingSm }}>
                        Estas cobranças existem no Asaas e nenhum item do sistema as reivindica. Diga o que cada uma
                        representa nesta locação: aponte para um item que já existe, ou escolha a categoria para
                        criar o lançamento. O que você deixar em branco continua como está.
                    </div>

                    {disponiveis.map((cobranca) => {
                        const escolhaAtual = vinculos[cobranca.id] || "";
                        const itemEscolhido = escolhaAtual ? itemPorChave.get(escolhaAtual) : undefined;
                        const trocaCobranca = !!itemEscolhido?.asaasPaymentIdAtual
                            && itemEscolhido.asaasPaymentIdAtual !== cobranca.id;

                        return (
                            <div
                                key={cobranca.id}
                                style={{
                                    padding: tokens.spacingMd,
                                    marginBottom: tokens.spacingSm,
                                    borderRadius: tokens.radiusLg,
                                    background: escolhaAtual ? tokens.primaryLight : tokens.bgWhite,
                                    border: `2px solid ${escolhaAtual ? tokens.primaryColor : tokens.borderDefault}`,
                                    transition: tokens.transitionBase,
                                }}
                            >
                                <div style={{ display: "flex", alignItems: "center", gap: tokens.spacingSm, flexWrap: "wrap" }}>
                                    <span style={{ fontWeight: 600, fontSize: tokens.fontLg, color: tokens.textPrimary }}>
                                        {formatarParaReais(cobranca.valor)}
                                    </span>
                                    <EtiquetaStatus cobranca={cobranca} />
                                    <span style={{ fontSize: tokens.fontSm, color: tokens.textSecondary }}>
                                        vence {formatarData(cobranca.vencimento)}
                                        {cobranca.estaPaga && cobranca.dataPagamento
                                            ? ` · paga em ${formatarData(cobranca.dataPagamento)}`
                                            : ""}
                                    </span>
                                    {cobranca.formaDePagamento && (
                                        <span style={{ fontSize: tokens.fontXs, color: tokens.textMuted }}>
                                            {FORMA_TEXTO[cobranca.formaDePagamento] || cobranca.formaDePagamento.toLowerCase()}
                                        </span>
                                    )}
                                    {cobranca.idAssinatura && (
                                        <Etiqueta texto="gerada por assinatura" cor={tokens.textSecondary} fundo={tokens.bgLight} />
                                    )}
                                </div>

                                {cobranca.descricao && (
                                    <div style={{ fontSize: tokens.fontSm, color: tokens.textSecondary, marginTop: tokens.spacingXs }}>
                                        {cobranca.descricao}
                                    </div>
                                )}

                                <div style={{ marginTop: tokens.spacingSm }}>
                                    <label style={{ fontSize: tokens.fontXs, color: tokens.textMuted, display: "block", marginBottom: 2 }}>
                                        Esta cobrança é
                                    </label>
                                    <select
                                        className="form-control"
                                        value={escolhaAtual}
                                        onChange={(e) => onAlterarVinculo(cobranca.id, e.target.value || null)}
                                        style={{ fontSize: tokens.fontSm }}
                                    >
                                        <option value="">Não trazer (deixar como está)</option>

                                        {/* Item existente vem primeiro para cobrança parecida com aluguel, que é o
                                            caso em que criar lançamento seria o erro; nas outras, criar vem antes. */}
                                        {cobranca.parecidaComAluguel && (
                                            <optgroup label="Apontar para um item que já existe">
                                                {itensOferecidos(escolhaAtual).map((item) => (
                                                    <option key={chaveDoItem(item)} value={chaveDoItem(item)}>{rotuloItem(item)}</option>
                                                ))}
                                            </optgroup>
                                        )}

                                        {categorias.length > 0 && (
                                            <optgroup label="Criar lançamento na categoria">
                                                {categorias.map((categoria) => (
                                                    <option key={categoria.id} value={chaveDaCategoria(categoria.id)}>
                                                        {categoria.nome}
                                                    </option>
                                                ))}
                                            </optgroup>
                                        )}

                                        {!cobranca.parecidaComAluguel && (
                                            <optgroup label="Apontar para um item que já existe">
                                                {itensOferecidos(escolhaAtual).map((item) => (
                                                    <option key={chaveDoItem(item)} value={chaveDoItem(item)}>{rotuloItem(item)}</option>
                                                ))}
                                            </optgroup>
                                        )}
                                    </select>
                                </div>

                                {cobranca.parecidaComAluguel && !escolhaAtual && (
                                    <div style={{ fontSize: tokens.fontXs, color: tokens.textMuted, marginTop: tokens.spacingXs }}>
                                        Parece cobrança de aluguel. Nesse caso aponte para a parcela do período, em vez
                                        de criar lançamento, para o contrato não ficar com a mesma semana em dois lugares.
                                    </div>
                                )}

                                {ehChaveDeCategoria(escolhaAtual) && (
                                    <div style={{ fontSize: tokens.fontXs, color: tokens.textSecondary, marginTop: tokens.spacingXs }}>
                                        Será criado um lançamento de {formatarParaReais(cobranca.valor)} com vencimento
                                        em {formatarData(cobranca.vencimento)}, já ligado a esta cobrança
                                        {cobranca.estaPaga ? " e marcado como pago" : ""}.
                                        {(() => {
                                            // Mesma regra do servidor: vencimentos da mesma assinatura na mesma
                                            // categoria viram um lançamento parcelado.
                                            const irmas = cobranca.idAssinatura
                                                ? disponiveis.filter((c) => c.idAssinatura === cobranca.idAssinatura && vinculos[c.id] === escolhaAtual).length
                                                : 0;
                                            return irmas > 1
                                                ? ` Com as outras desta assinatura nesta categoria, vira um lançamento parcelado em ${irmas} parcelas.`
                                                : "";
                                        })()}
                                    </div>
                                )}

                                {cobranca.estaPaga && escolhaAtual && (
                                    <div style={{ fontSize: tokens.fontXs, color: "#1b6b3a", marginTop: tokens.spacingXs }}>
                                        O item será marcado como pago com a data desta cobrança
                                        {cobranca.dataPagamento ? ` (${formatarData(cobranca.dataPagamento)})` : ""}.
                                    </div>
                                )}

                                {trocaCobranca && (
                                    <div style={{ ...alerta, marginTop: tokens.spacingXs }}>
                                        Este item já aponta para outra cobrança no Asaas. Ao confirmar, aquela será
                                        removida, se ainda estiver em aberto, para o cliente não receber duas.
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </>
            )}

            {comDono.length > 0 && (
                <details style={{ marginTop: tokens.spacingMd }}>
                    <summary style={{ cursor: "pointer", fontSize: tokens.fontSm, color: tokens.textSecondary }}>
                        Cobranças que já têm dono no sistema ({comDono.length})
                    </summary>
                    <div style={{ marginTop: tokens.spacingSm }}>
                        {comDono.map((cobranca) => (
                            <div
                                key={cobranca.id}
                                style={{
                                    display: "flex", gap: tokens.spacingSm, flexWrap: "wrap", alignItems: "center",
                                    padding: tokens.spacingSm, borderBottom: `1px solid ${tokens.borderLight}`,
                                    fontSize: tokens.fontSm, color: tokens.textSecondary,
                                }}
                            >
                                <span style={{ fontWeight: 600 }}>{formatarParaReais(cobranca.valor)}</span>
                                <EtiquetaStatus cobranca={cobranca} />
                                <span>vence {formatarData(cobranca.vencimento)}</span>
                                <span style={{ color: cobranca.donoEhDestaLocacao ? tokens.textSecondary : "#8a5a00" }}>
                                    {cobranca.donoEhDestaLocacao ? "desta locação" : "de outro registro"}
                                    {cobranca.donoDescricao ? `: ${cobranca.donoDescricao}` : ""}
                                </span>
                            </div>
                        ))}
                    </div>
                </details>
            )}
        </div>
    );
}
